<template>
  <span class="text-diff" :class="{ strikethrough }">
    <template v-for="(part, index) in diffs" :key="index">
      <span class="c" v-if="part.type === 'common'">
        <template v-for="(segment, segmentIndex) in highlightSegments(part.text)" :key="segmentIndex">
          <mark v-if="segment.matched" class="search-highlight">{{ segment.text }}</mark>
          <template v-else>{{ segment.text }}</template>
        </template>
      </span>
      <span class="d" v-else-if="part.type === 'removed' && onlyAdded !== true && hasVisibleText(part.text)">
        <template v-for="(segment, segmentIndex) in highlightSegments(part.text)" :key="segmentIndex">
          <mark v-if="segment.matched" class="search-highlight">{{ segment.text }}</mark>
          <template v-else>{{ segment.text }}</template>
        </template>
      </span>
      <span
        v-else-if="shouldShowHiddenRemovedMarker(part, index)"
        class="hidden-removed-marker"
        :title="hiddenRemovedMarkerTitle(part)" />
      <span
        class="a"
        v-else-if="part.type === 'added' && onlyRemoved !== true && hasVisibleText(part.text)"
        :title="addedPartTitle(index)">
        <template v-for="(segment, segmentIndex) in highlightSegments(part.text)" :key="segmentIndex">
          <mark v-if="segment.matched" class="search-highlight">{{ segment.text }}</mark>
          <template v-else>{{ segment.text }}</template>
        </template>
      </span>
    </template>
  </span>
</template>

<script lang="ts" setup>

const props = defineProps<{
  textA: string
  textB: string
  onlyAdded?: boolean
  onlyRemoved?: boolean
  strikethrough?: boolean
  caseSensitive?: boolean
  highlightKeyword?: string
  showHiddenRemovedMarker?: boolean
}>()

const { textA, textB, onlyAdded, onlyRemoved, strikethrough, caseSensitive, highlightKeyword, showHiddenRemovedMarker } = toRefs(props)
const diffs = computed(() => {
  const [normalizedA, normalizedB] = normalizeComparablePair(textA.value, textB.value)
  return diffStringsSmart(normalizedA, normalizedB, caseSensitive.value === true)
})

type DiffPart = { type: "common" | "added" | "removed"; text: string };
type HighlightSegment = { text: string; matched: boolean };
const maxCharRefineLength = 32
/** Email 一律小寫；地址等全大寫來源才統一大寫，避免大小寫格式差異造成破碎 diff。 */
function normalizeComparablePair(a: string, b: string): [string, string] {
  if (isEmailLikeSource(a) || isEmailLikeSource(b)) return [a.toLowerCase(), b.toLowerCase()]
  return normalizeAllCapsPair(a, b)
}

/** 任一側是全大寫英文資料時，兩側都用大寫比對與顯示，避免大小寫格式差異造成破碎 diff。 */
function normalizeAllCapsPair(a: string, b: string): [string, string] {
  if (!isAllCapsLatinSource(a) && !isAllCapsLatinSource(b)) return [a, b]
  return [a.toUpperCase(), b.toUpperCase()]
}

function isEmailLikeSource(value: string) {
  return /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/.test(value)
}

function isAllCapsLatinSource(value: string) {
  return /[A-Z]/.test(value) && !/[a-z]/.test(value)
}

/** 將搜尋字切成純文字片段，讓 diff 背景色和搜尋標示可同時存在。 */
function highlightSegments(text: string): HighlightSegment[] {
  const query = highlightKeyword.value?.trim()
  if (!query) return [{ text, matched: false }]
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const segments: HighlightSegment[] = []
  let currentIndex = 0
  let matchedIndex = lowerText.indexOf(lowerQuery)

  while (matchedIndex !== -1) {
    if (matchedIndex > currentIndex) {
      segments.push({ text: text.slice(currentIndex, matchedIndex), matched: false })
    }
    segments.push({ text: text.slice(matchedIndex, matchedIndex + query.length), matched: true })
    currentIndex = matchedIndex + query.length
    matchedIndex = lowerText.indexOf(lowerQuery, currentIndex)
  }

  if (currentIndex < text.length) {
    segments.push({ text: text.slice(currentIndex), matched: false })
  }
  return segments
}

function shouldShowHiddenRemovedMarker(part: DiffPart, index: number) {
  return part.type === 'removed'
    && onlyAdded.value === true
    && showHiddenRemovedMarker.value === true
    && diffs.value[index - 1]?.type !== 'removed'
}

function hiddenRemovedMarkerTitle(part: DiffPart) {
  return `原先刪除：${part.text}`
}

function hasVisibleText(text: string) {
  return text.trim().length > 0
}

function addedPartTitle(index: number) {
  const previous = diffs.value[index - 1]
  if (onlyAdded.value !== true || showHiddenRemovedMarker.value !== true || previous?.type !== 'removed') return undefined
  return hiddenRemovedMarkerTitle(previous)
}

function diffStringsSmart(a: string, b: string, caseSensitive = true): DiffPart[] {
  const normalize = (s: string) => (caseSensitive ? s : s.toLowerCase());
  const keepTokenLevel = isEmailLikeSource(a) || isEmailLikeSource(b)

  // ---- 修正版 fastPath：common 一律取自 b 的原文 ----
  function fastPath(): DiffPart[] | null {
    // 內容相同（可能只有大小寫不同）：common 要用 b 的原文
    if (normalize(a) === normalize(b)) {
      return [{ type: "common", text: b }];
    }

    // a 是 b 的子字串（純插入）：common 是 b 內那段原文
    const idxInB = normalize(b).indexOf(normalize(a));
    if (idxInB !== -1) {
      const pre = b.slice(0, idxInB);
      const common = b.slice(idxInB, idxInB + a.length); // 取 b 原文
      const post = b.slice(idxInB + a.length);

      const out: DiffPart[] = [];
      if (pre) out.push({ type: "added", text: pre });
      out.push({ type: "common", text: common });
      if (post) out.push({ type: "added", text: post });
      return out;
    }

    // b 是 a 的子字串（純刪除）：common 直接用 b（保留 b 的原文大小寫）
    const idxInA = normalize(a).indexOf(normalize(b));
    if (idxInA !== -1) {
      const pre = a.slice(0, idxInA);
      const post = a.slice(idxInA + b.length);

      const out: DiffPart[] = [];
      if (pre) out.push({ type: "removed", text: pre });
      out.push({ type: "common", text: b }); // 不用 a.slice(...)，改用 b
      if (post) out.push({ type: "removed", text: post });
      return out;
    }

    return null;
  }

  function tokenize(s: string): string[] {
    const re = /[A-Za-z0-9]+|\s+|[^A-Za-z0-9\s]/g;
    return s.match(re) ?? [];
  }

  function tokenizeChars(s: string): string[] {
    return Array.from(s)
  }

  function mergeDiffParts(parts: DiffPart[]): DiffPart[] {
    const out: DiffPart[] = []
    parts.forEach((part) => {
      if (!part.text) return
      const last = out[out.length - 1]
      if (last && last.type === part.type) last.text += part.text
      else out.push({ ...part })
    })
    return out
  }

  function diffByUnits(A: string[], B: string[]): DiffPart[] {
    const m = A.length, n = B.length;

    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
      for (let j = n - 1; j >= 0; j--) {
        dp[i][j] =
          normalize(A[i]) === normalize(B[j])
            ? dp[i + 1][j + 1] + 1
            : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }

    const out: DiffPart[] = [];
    let i = 0, j = 0;
    const push = (type: DiffPart["type"], text: string) => {
      if (!text) return;
      const last = out[out.length - 1];
      if (last && last.type === type) last.text += text;
      else out.push({ type, text });
    };

    while (i < m && j < n) {
      if (normalize(A[i]) === normalize(B[j])) {
        push("common", B[j]); // 永遠保留 b 的原文
        i++; j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        push("removed", A[i]); i++;
      } else {
        push("added", B[j]); j++;
      }
    }
    while (i < m) push("removed", A[i++]);
    while (j < n) push("added", B[j++]);

    return out;
    }

  function diffByChars(charA: string, charB: string): DiffPart[] {
    return diffByUnits(tokenizeChars(charA), tokenizeChars(charB))
  }

  function shouldRefinePairByChars(removed: string, added: string) {
    if (keepTokenLevel) return shouldRefineSimilarEmailTokenPair(removed, added)
    const compactPair = `${removed}${added}`.replace(/\s/g, '')
    const hasSpaces = /\s/.test(`${removed}${added}`)
    if (!hasSpaces) return compactPair.length <= maxCharRefineLength * 2
    return removed.length <= maxCharRefineLength && added.length <= maxCharRefineLength
  }

  function shouldRefineSimilarEmailTokenPair(removed: string, added: string) {
    const removedToken = removed.trim()
    const addedToken = added.trim()
    if (!/^[A-Za-z0-9]+$/.test(removedToken) || !/^[A-Za-z0-9]+$/.test(addedToken)) return false
    if (Math.min(removedToken.length, addedToken.length) < 4) return false
    if (Math.abs(removedToken.length - addedToken.length) > 2) return false
    return editDistance(normalize(removedToken), normalize(addedToken)) <= 2
  }

  function editDistance(left: string, right: string) {
    const dp = Array.from({ length: left.length + 1 }, () => Array(right.length + 1).fill(0))
    for (let i = 0; i <= left.length; i++) dp[i][0] = i
    for (let j = 0; j <= right.length; j++) dp[0][j] = j

    for (let i = 1; i <= left.length; i++) {
      for (let j = 1; j <= right.length; j++) {
        const substitutionCost = left[i - 1] === right[j - 1] ? 0 : 1
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + substitutionCost,
        )
      }
    }

    return dp[left.length][right.length]
  }

  /** 先用 token 找穩定錨點，再只把相鄰的刪除/新增區塊細拆成字元差異。 */
  function refineChangedPairs(parts: DiffPart[]): DiffPart[] {
    const out: DiffPart[] = []
    for (let index = 0; index < parts.length; index++) {
      const current = parts[index]
      const next = parts[index + 1]
      if (current?.type === 'removed' && next?.type === 'added') {
        out.push(...(shouldRefinePairByChars(current.text, next.text)
          ? diffByChars(current.text, next.text)
          : [current, next]))
        index++
        continue
      }
      if (current?.type === 'added' && next?.type === 'removed') {
        out.push(...(shouldRefinePairByChars(next.text, current.text)
          ? diffByChars(next.text, current.text)
          : [next, current]))
        index++
        continue
      }
      out.push(current)
    }
    return mergeDiffParts(out)
  }

  return fastPath() ?? refineChangedPairs(diffByUnits(tokenize(a), tokenize(b)));
}

</script>

<style lang="stylus" scoped>
.strikethrough .d
  text-decoration line-through
.d
  background-color #FCE4EC
  border-radius 2px
.a
  background-color #E0F2F1
  border-radius 2px
.search-highlight
  color #C2410C
  background #FFEDD5
  border 1px solid #FDBA74
  border-radius 3px
  padding 0 2px
.hidden-removed-marker
  display inline-block
  width 2px
  height 1.2em
  margin 0 0 0 -0.5px
  border-radius 2px
  background #F8BBD0
  vertical-align -0.18em
</style>
